import { useState, useCallback } from 'react'
import type { PetType, AiConfig, SearchResult, SymptomSummary } from '../lib/types'
import { searchSymptoms, summarizePages, generatePdf, ApiError } from '../lib/api'
import DOMPurify from 'dompurify'

interface UseSymptomSearchState {
  isSearching: boolean
  isSummarizing: boolean
  isGeneratingPdf: boolean
  results: SearchResult[]
  summary: SymptomSummary | null
  error: string | null
}

interface UseSymptomSearchReturn extends UseSymptomSearchState {
  search: (keyword: string, petType: PetType) => Promise<void>
  summarize: (urls: string[], keyword: string, petType: PetType, aiConfig: AiConfig) => Promise<void>
  downloadPdf: (summary: SymptomSummary, userMemo?: string) => Promise<void>
  reset: () => void
}

export function useSymptomSearch(): UseSymptomSearchReturn {
  const [state, setState] = useState<UseSymptomSearchState>({
    isSearching: false,
    isSummarizing: false,
    isGeneratingPdf: false,
    results: [],
    summary: null,
    error: null,
  })

  const search = useCallback(async (keyword: string, petType: PetType) => {
    setState((prev) => ({
      ...prev,
      isSearching: true,
      error: null,
      results: [],
      summary: null,
    }))

    try {
      const response = await searchSymptoms(keyword, petType)
      setState((prev) => ({
        ...prev,
        isSearching: false,
        results: response.results,
      }))
    } catch (error) {
      const message =
        error instanceof ApiError
          ? error.message
          : '検索中にエラーが発生しました'
      setState((prev) => ({
        ...prev,
        isSearching: false,
        error: message,
      }))
    }
  }, [])

  const summarize = useCallback(
    async (urls: string[], keyword: string, petType: PetType, aiConfig: AiConfig) => {
      setState((prev) => ({
        ...prev,
        isSummarizing: true,
        error: null,
      }))

      try {
        const response = await summarizePages(urls, keyword, petType, aiConfig)
        setState((prev) => ({
          ...prev,
          isSummarizing: false,
          summary: response.summary,
        }))
      } catch (error) {
        const message =
          error instanceof ApiError
            ? error.message
            : '要約中にエラーが発生しました'
        setState((prev) => ({
          ...prev,
          isSummarizing: false,
          error: message,
        }))
      }
    },
    []
  )

  const downloadPdf = useCallback(async (summary: SymptomSummary, userMemo?: string) => {
    setState((prev) => ({
      ...prev,
      isGeneratingPdf: true,
      error: null,
    }))

    try {
      const { html, filename } = await generatePdf(summary, { userMemo })

      // DOMPurifyでHTMLをサニタイズ
      const sanitizedHtml = DOMPurify.sanitize(html, {
        USE_PROFILES: { html: true },
        ADD_TAGS: ['style'],
      })

      // html2pdfを使用してPDFを生成
      const { default: html2pdf } = await import('html2pdf.js')

      const container = document.createElement('div')
      container.insertAdjacentHTML('beforeend', sanitizedHtml)
      document.body.appendChild(container)

      await html2pdf()
        .set({
          margin: 10,
          filename,
          image: { type: 'jpeg', quality: 0.98 },
          html2canvas: { scale: 2, useCORS: true },
          jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
        })
        .from(container)
        .save()

      document.body.removeChild(container)

      setState((prev) => ({
        ...prev,
        isGeneratingPdf: false,
      }))
    } catch (error) {
      const message =
        error instanceof ApiError
          ? error.message
          : 'PDF生成中にエラーが発生しました'
      setState((prev) => ({
        ...prev,
        isGeneratingPdf: false,
        error: message,
      }))
    }
  }, [])

  const reset = useCallback(() => {
    setState({
      isSearching: false,
      isSummarizing: false,
      isGeneratingPdf: false,
      results: [],
      summary: null,
      error: null,
    })
  }, [])

  return {
    ...state,
    search,
    summarize,
    downloadPdf,
    reset,
  }
}
